import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { usePlan } from "@/context/PlanContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshPlan } = usePlan();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0, cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setState("paid"); refreshPlan(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { if (!cancelled) { setState("error"); return; } }
      if (++attempts >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshPlan]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      <Card className="bg-[#111723] border-[#222F43] p-10">
        {state === "checking" && (<>
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold">Confirming with Stripe…</h2>
          <p className="text-slate-400 text-sm mt-2">This usually takes a couple of seconds.</p>
        </>)}
        {state === "paid" && (<>
          <div className="h-14 w-14 rounded-2xl bg-emerald-500/12 flex items-center justify-center mx-auto mb-4"><Crown className="h-7 w-7 text-emerald-400" /></div>
          <h2 className="font-display text-2xl font-bold" data-testid="payment-paid">You're Pro.</h2>
          <p className="text-slate-400 text-sm mt-2">Your free trial has started. Scan without limits and go star some catalysts.</p>
          <div className="flex gap-2 justify-center mt-6">
            <Button asChild className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"><Link to="/scanner" data-testid="success-scan-link">Start scanning</Link></Button>
            <Button asChild variant="outline" className="border-[#334566] bg-transparent"><Link to="/catalysts">Catalyst Calendar</Link></Button>
          </div>
        </>)}
        {(state === "timeout" || state === "error") && (<>
          <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold">{state === "timeout" ? "Still processing" : "Something went sideways"}</h2>
          <p className="text-slate-400 text-sm mt-2">{state === "timeout" ? "Stripe is taking longer than usual. Your access will unlock automatically — check back in a minute." : "We couldn't confirm this checkout. If you were charged, email us and we'll sort it."}</p>
          <Button asChild variant="outline" className="border-[#334566] bg-transparent mt-6"><Link to="/pricing">Back to pricing</Link></Button>
        </>)}
      </Card>
    </div>
  );
}
